import { MATURA_TASK_TYPE_CODE3 } from "../maturaTaskClassification";
import { getMaturaCourseCode } from "../maturaCourseStructure";
import { buildMaturaCoursePriorityRows } from "./maturaHistoryAnalysis";

const RECENT_ATTEMPT_WEIGHTS = [1, 0.75, 0.5];
const FOUNDATION_RESULT_THRESHOLD = 25;

const finiteNumber = (...values) => {
  const match = values.find((value) => (
    value !== null
    && value !== undefined
    && value !== ""
    && Number.isFinite(Number(value))
  ));
  return match === undefined ? null : Number(match);
};

function buildTopicLookups(courseStructure) {
  const topicByCode = new Map();
  const courseCodeBySource = new Map();

  (courseStructure || []).forEach((section) => {
    (section?.topics || []).forEach((topic) => {
      topicByCode.set(topic.code, {
        ...topic,
        sectionNo: section.sectionNo,
      });
      courseCodeBySource.set(topic.code, topic.code);
      (topic.sourceCodes || []).forEach((code) => courseCodeBySource.set(code, topic.code));
    });
  });

  return { topicByCode, courseCodeBySource };
}

function resolveQuestionCourseCode(question, courseCodeBySource) {
  const typeKey = question?.quizTypeKey || question?.type || question?.task?.quizTypeKey;
  const rawCode = question?.code3
    || question?.task?.code3
    || MATURA_TASK_TYPE_CODE3[typeKey]
    || null;
  if (!rawCode) return null;

  const canonicalCode = getMaturaCourseCode(rawCode);
  return courseCodeBySource.get(rawCode)
    || courseCodeBySource.get(canonicalCode)
    || canonicalCode;
}

function getQuestionPoints(question) {
  const maxPoints = Math.max(0, finiteNumber(
    question?.maxScore,
    question?.maxPoints,
    question?.task?.points,
    question?.task?.maxPoints,
    0
  ) || 0);
  const earnedPoints = Math.min(maxPoints, Math.max(0, finiteNumber(
    question?.score,
    question?.pointsEarned,
    question?.isCorrect ? maxPoints : 0
  ) || 0));

  return {
    maxPoints,
    earnedPoints,
    missedPoints: Math.max(0, maxPoints - earnedPoints),
  };
}

export function buildMaturaAttemptRecommendations({
  snapshots,
  courseStructure,
  officialSheets,
  targetCodes,
  limit = 2,
}) {
  const recentSnapshots = (Array.isArray(snapshots) ? snapshots : [])
    .filter((snapshot) => Array.isArray(snapshot?.questions) && snapshot.questions.length)
    .slice(0, RECENT_ATTEMPT_WEIGHTS.length);
  if (!recentSnapshots.length) {
    return {
      attemptsAnalyzed: 0,
      recommendations: [],
    };
  }

  const { topicByCode, courseCodeBySource } = buildTopicLookups(courseStructure);
  const priorityByCode = new Map(
    buildMaturaCoursePriorityRows(courseStructure, officialSheets)
      .map((row) => [row.code3, row])
  );
  const resultByCode = new Map();
  let weightedPointsEarned = 0;
  let weightedMaxPoints = 0;

  recentSnapshots.forEach((snapshot, snapshotIndex) => {
    const weight = RECENT_ATTEMPT_WEIGHTS[snapshotIndex];
    const seenInAttempt = new Set();

    snapshot.questions.forEach((question) => {
      const points = getQuestionPoints(question);
      if (points.maxPoints <= 0) return;

      weightedPointsEarned += points.earnedPoints * weight;
      weightedMaxPoints += points.maxPoints * weight;

      const code = resolveQuestionCourseCode(question, courseCodeBySource);
      const topic = topicByCode.get(code);
      if (!topic) return;

      if (!resultByCode.has(code)) {
        resultByCode.set(code, {
          code,
          title: topic.title,
          sectionNo: Number(topic.sectionNo) || 99,
          minimumTarget: Number(topic.minimumTarget) || 100,
          missedPoints: 0,
          availablePoints: 0,
          missedAttempts: 0,
          attemptsSeen: 0,
          tasksPassed: 0,
          tasksSeen: 0,
          pointsEarned: 0,
          pointsAvailable: 0,
        });
      }

      const result = resultByCode.get(code);
      result.missedPoints += points.missedPoints * weight;
      result.availablePoints += points.maxPoints * weight;
      if (points.missedPoints > 0) result.missedAttempts += 1;
      result.tasksSeen += 1;
      if (points.earnedPoints === points.maxPoints) result.tasksPassed += 1;
      result.pointsEarned += points.earnedPoints;
      result.pointsAvailable += points.maxPoints;
      if (!seenInAttempt.has(code)) {
        result.attemptsSeen += 1;
        seenInAttempt.add(code);
      }
    });
  });

  const performancePercent = weightedMaxPoints > 0
    ? (weightedPointsEarned / weightedMaxPoints) * 100
    : 0;
  const needsFoundation = weightedMaxPoints > 0
    && performancePercent < FOUNDATION_RESULT_THRESHOLD;
  const topicResults = Array.from(resultByCode.values()).map((result) => ({
    ...result,
    masteryRate: result.pointsAvailable > 0 ? result.pointsEarned / result.pointsAvailable : 0,
    isMastered: result.pointsAvailable > 0
      && result.pointsEarned / result.pointsAvailable >= 2 / 3,
  }));
  const masteredTopics = topicResults
    .filter((result) => result.isMastered)
    .sort((a, b) => (
      b.masteryRate - a.masteryRate
      || b.tasksSeen - a.tasksSeen
      || a.sectionNo - b.sectionNo
      || a.title.localeCompare(b.title, "pl")
    ));

  const compareRecommendations = (a, b) => (
    a.minimumTarget - b.minimumTarget
    || b.score - a.score
    || a.sectionNo - b.sectionNo
    || b.missedPoints - a.missedPoints
    || a.title.localeCompare(b.title, "pl")
  );
  const preferredCodes = targetCodes instanceof Set
    ? targetCodes
    : Array.isArray(targetCodes)
      ? new Set(targetCodes)
      : null;
  const isInPreferredScope = (code) => !preferredCodes || preferredCodes.has(code);

  const enriched = topicResults
    .filter((result) => !result.isMastered && result.missedPoints > 0)
    .map((result) => {
      const priority = priorityByCode.get(result.code) || {};
      const easeWeight = 1 + (100 - result.minimumTarget) / 100;
      const recurrenceWeight = 1 + Math.max(0, result.attemptsSeen - 1) * 0.35;
      const examValue = Number(priority.averagePointsPerSheet) || 0;
      const foundationWeight = needsFoundation
        ? (result.minimumTarget <= 50 ? 1.65 : 0.55)
        : 1;
      return {
        ...result,
        examValue,
        occurrenceRate: Number(priority.occurrenceRate) || 0,
        score: (result.missedPoints * easeWeight * recurrenceWeight
          + examValue * 0.3
          + (Number(priority.occurrenceRate) || 0) * 0.2) * foundationWeight,
      };
    })
    .sort(compareRecommendations);

  const masteredCodes = new Set(masteredTopics.map((item) => item.code));
  const fallbacks = buildMaturaCoursePriorityRows(courseStructure, officialSheets)
      .filter((row) => (
        topicByCode.has(row.code3)
        && !masteredCodes.has(row.code3)
        && !enriched.some((item) => item.code === row.code3)
      ))
      .map((row) => ({
        code: row.code3,
        title: topicByCode.get(row.code3).title,
        sectionNo: Number(topicByCode.get(row.code3).sectionNo) || 99,
        minimumTarget: Number(topicByCode.get(row.code3).minimumTarget) || 100,
        missedPoints: 0,
        availablePoints: 0,
        missedAttempts: 0,
        attemptsSeen: 0,
        tasksPassed: 0,
        tasksSeen: 0,
        pointsEarned: 0,
        pointsAvailable: 0,
        masteryRate: 0,
        isMastered: false,
        examValue: Number(row.averagePointsPerSheet) || 0,
        occurrenceRate: Number(row.occurrenceRate) || 0,
        score: 0,
        fallback: true,
      }))
      .sort((a, b) => (
        a.minimumTarget - b.minimumTarget
        || b.examValue - a.examValue
        || b.occurrenceRate - a.occurrenceRate
      ));

  const recommendations = [];
  const appendUntilLimit = (items) => {
    if (recommendations.length >= limit) return;
    recommendations.push(...items.slice(0, limit - recommendations.length));
  };

  // Najpierw rzeczywiste braki z wybranego zakresu, od prostszych warstw.
  appendUntilLimit(enriched.filter((item) => isInPreferredScope(item.code)));
  // Druga opcja nadal ma należeć do ścieżki ucznia, nawet jeśli brakuje jej
  // jeszcze wystarczającej próby w ostatnich arkuszach.
  appendUntilLimit(fallbacks.filter((item) => isInPreferredScope(item.code)));
  // Poza zakres sięgamy dopiero po wyczerpaniu sensownych tematów z planu.
  appendUntilLimit(enriched.filter((item) => !isInPreferredScope(item.code)));
  appendUntilLimit(fallbacks.filter((item) => !isInPreferredScope(item.code)));

  return {
    attemptsAnalyzed: recentSnapshots.length,
    performancePercent,
    needsFoundation,
    topicResults,
    masteredTopics,
    recommendations,
  };
}
